import authService from './authService';
import { SSEEvent, SSECompleteEvent, SessionResponse, SessionsResponse, ChartContext } from '@/types/query';

const API_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is not set');
}

class QueryService {
  /**
   * Stream a query with SSE
   */
  async streamQuery(
    question: string,
    sessionId: string | null,
    chartContext: ChartContext | null,
    onProgress: (event: SSEEvent) => void,
    onComplete: (event: SSECompleteEvent) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    try {
      const response = await fetch(`${API_URL}/api/admin/query/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question,
          sessionId,
          ...(chartContext ? { chartContext } : {})
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Process all complete events in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let currentEvent: string | null = null;
        let currentData: string[] = [];

        for (const line of lines) {
          if (line.startsWith('event:')) {
            // New event type
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            // Event data
            currentData.push(line.substring(5).trim());
          } else if (line.trim() === '' && currentEvent) {
            // End of event - parse and emit
            try {
              const dataStr = currentData.join('\n');
              const data = JSON.parse(dataStr);
              
              const event: SSEEvent = {
                type: currentEvent as any,
                data
              };

              // Handle complete event specially
              if (currentEvent === 'complete') {
                onComplete(data as SSECompleteEvent);
              } else if (currentEvent === 'error') {
                onError(new Error(data.message || 'Query error'));
              } else {
                onProgress(event);
              }
            } catch (err) {
              console.error('Failed to parse SSE data:', err);
            }

            // Reset for next event
            currentEvent = null;
            currentData = [];
          }
        }
      }
    } catch (error) {
      console.error('Stream query error:', error);
      onError(error as Error);
    }
  }

  /**
   * Get a session with all messages
   */
  async getSession(sessionId: string): Promise<SessionResponse> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/query/sessions/${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all sessions for current user
   */
  async getSessions(): Promise<SessionsResponse> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/query/sessions`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load sessions: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a session title
   */
  async updateSession(sessionId: string, title: string): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/query/sessions/${sessionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ title })
    });

    if (!response.ok) {
      throw new Error(`Failed to update session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/query/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.statusText}`);
    }

    return response.json();
  }
}

export default new QueryService();
